import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Form } from 'react-final-form';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import EnquiryListingContext from '../../providers/EnquiryListing';
import { Loader } from 'ncoded-component-library';
import ConfirmDetails from './components/ConfirmDetails';
import { useOnEnquire } from '../../../../hooks';
import EnquiryQuestions from './components/EnquiryQuestions';
import { EnquiryFormValues } from '../../utils';
import { getShouldAnswerQuestionsOnEnquire } from 'utils';
import { UserEnquiryContext } from 'models/UserEnquiryContext';
import useEnquiryNameSugestion from './hooks/useEnquiryNameSuggestion';

import './RegistrationOfInterestForm.styles.scss';
import './RegistrationOfInterestForm.styles.responsive.scss';

const RegistrationOfInterestForm: React.FC = () => {
  const [step, setStep] = useState<number>(1);

  const [shouldSaveContext, setShouldSaveContext] = useState(false);
  const [selectedContext, setSelectedContext] = useState<UserEnquiryContext>();

  const {
    currentUser: { firstName, lastName, email, contactNumber },
  } = useContext(CurrentUserContext);
  const { listing } = useContext(EnquiryListingContext);

  const { qualificationQuestions } = listing?.dealRoom || {};

  const shouldAnswerQuestions = getShouldAnswerQuestionsOnEnquire(
    listing?.dealRoom,
  );

  const suggestedTagForNewContext = useEnquiryNameSugestion(listing?.industry);

  const classes = classNames('registration-of-interest-form');

  const initialValues = useMemo(
    () => ({
      firstName,
      lastName,
      email,
      contactNumber,
      ...(shouldAnswerQuestions && {
        questions: qualificationQuestions.map(({ title }) => ({
          title,
          answer: '',
        })),
      }),
    }),
    [
      contactNumber,
      email,
      firstName,
      lastName,
      qualificationQuestions,
      shouldAnswerQuestions,
    ],
  );

  const handleOnLoggedInEnquire = useOnEnquire(listing);

  const contextInfo = useMemo(
    () => ({
      shouldSaveContext,
      selectedContext,
      suggestedTagForNewContext,
    }),
    [selectedContext, shouldSaveContext, suggestedTagForNewContext],
  );

  const onSubmit = useCallback(
    async (values: EnquiryFormValues) =>
      await handleOnLoggedInEnquire(values, contextInfo),
    [contextInfo, handleOnLoggedInEnquire],
  );

  const nextStep = useCallback(() => setStep((old) => old + 1), []);
  const onBack = useCallback(() => setStep((old) => old - 1), []);

  return (
    <div className={classes}>
      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        render={({
          submitting,
          invalid,
          values: { context },
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            {submitting && <Loader />}

            {step === 1 && (
              <ConfirmDetails
                invalid={invalid}
                context={context}
                isContextRequired={!!listing?.isContextRequired}
                suggestedTagForNewContext={suggestedTagForNewContext}
                shouldSaveContext={shouldSaveContext}
                selectedContext={selectedContext}
                setShouldSaveContext={setShouldSaveContext}
                setSelectedContext={setSelectedContext}
                {...(shouldAnswerQuestions && { nextStep })}
              />
            )}

            {step === 2 && (
              <EnquiryQuestions invalid={invalid} onBack={onBack} />
            )}
          </form>
        )}
      />
    </div>
  );
};

export default RegistrationOfInterestForm;
